import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import AgeGate from "./components/AgeGate";
import Header from "./components/Header";
import CartSheet from "./components/CartSheet";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CheckoutPage from "./pages/CheckoutPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import OrderConfirmation from "./pages/OrderConfirmation";
import AdminPage from "./pages/AdminPage";
import OrderLookupPage from "./pages/OrderLookupPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem("airo_session_id");
  if (!sessionId) {
    sessionId = "session_" + Math.random().toString(36).substr(2, 9);
    localStorage.setItem("airo_session_id", sessionId);
  }
  return sessionId;
};

function App() {
  const [isAgeVerified, setIsAgeVerified] = useState(false);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sessionId] = useState(getSessionId());

  useEffect(() => {
    const verified = localStorage.getItem("airo_age_verified");
    if (verified === "true") {
      setIsAgeVerified(true);
    }
  }, []);

  useEffect(() => {
    if (isAgeVerified) {
      fetchCart();
      seedProducts();
    }
  }, [isAgeVerified]);

  const seedProducts = async () => {
    try {
      await axios.post(`${API}/seed`);
    } catch (e) {
      console.log("Products may already be seeded");
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (e) {
      console.error("Error fetching cart:", e);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      await axios.post(`${API}/cart/add`, {
        session_id: sessionId,
        product_id: productId,
        quantity,
      });
      await fetchCart();
      setIsCartOpen(true);
    } catch (e) {
      console.error("Error adding to cart:", e);
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      await axios.put(`${API}/cart/update`, {
        session_id: sessionId,
        product_id: productId,
        quantity,
      });
      await fetchCart();
    } catch (e) {
      console.error("Error updating cart:", e);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove`, {
        data: { session_id: sessionId, product_id: productId },
      });
      await fetchCart();
    } catch (e) {
      console.error("Error removing from cart:", e);
    }
  };

  const handleAgeVerify = () => {
    localStorage.setItem("airo_age_verified", "true");
    setIsAgeVerified(true);
  };

  const handleAgeDeny = () => {
    window.location.href = "https://www.google.com";
  };

  if (!isAgeVerified) {
    return <AgeGate onVerify={handleAgeVerify} onDeny={handleAgeDeny} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <Header cart={cart} onCartClick={() => setIsCartOpen(true)} />
        <CartSheet
          cart={cart}
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          onUpdateItem={updateCartItem}
          onRemoveItem={removeFromCart}
        />
        <main>
          <Routes>
            <Route path="/" element={<HomePage addToCart={addToCart} />} />
            <Route path="/shop" element={<ShopPage addToCart={addToCart} />} />
            <Route path="/shop/:category" element={<ShopPage addToCart={addToCart} />} />
            <Route path="/product/:productId" element={<ProductPage addToCart={addToCart} />} />
            <Route path="/checkout" element={<CheckoutPage cart={cart} sessionId={sessionId} onOrderComplete={fetchCart} />} />
            <Route path="/order/:orderId" element={<OrderConfirmation />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/track-order" element={<OrderLookupPage />} />
          </Routes>
        </main>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
